// react
import { useContext, useEffect, useMemo, useState } from 'react';

// router
import { useSearchParams } from 'react-router-dom';

// types
import { Account, Payment, Transaction } from '../@types';

// hooks
import { useFetch } from './useFetch';

// config
import { featureFlags } from '../config/featureFlags';

// context
import { ContextProject } from '../components/pages/Project/Project.context';

// props
export interface UseReimbursementsProps {
	account?: Account | undefined;
	fieldsReimbursements?: string;
	fieldsTransactionsApplied?: string;
	reimbursementIds?: number[];
}

export const useReimbursements = ({
	account,
	fieldsReimbursements = 'id,payments,transactions',
	fieldsTransactionsApplied = 'id,payments,type',
	reimbursementIds = [],
}: UseReimbursementsProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();

	// params
	const paramDialogDateEnd = searchParams.get('dialogDateEnd');
	const paramDialogDateStart = searchParams.get('dialogDateStart');

	// context
	const { project } = useContext(ContextProject);

	// state
	const [reimbursements, setReimbursements] = useState<Transaction[]>([]);
	const [transactionsApplied, setTransactionsApplied] = useState<Transaction[]>([]);

	// fetch reimbursements
	let urlFetchReimbursements = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&type=REIMBURSEMENT&fields=${fieldsReimbursements}`;

	if (account) {
		urlFetchReimbursements += `&toAccount=${account.id}`;
	} else if (reimbursementIds.length) {
		urlFetchReimbursements += `&id=${reimbursementIds.join(',')}`;
	}

	const { isLoading: isLoadingReimbursements, refetch: refetchReimbursements } = useFetch({
		isEnabled: featureFlags['REIMBURSEMENT'] && Boolean(account?.id || reimbursementIds.length),
		url: urlFetchReimbursements,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			console.log('Reimbursement transactions: ', res.data);
			if (res.data) {
				setReimbursements(res.data);
			}
		},
	});

	const transactionIds = useMemo(() => {
		const transactionsAppliedNew = [];

		for (let i = 0; i < reimbursements.length; i++) {
			const reimbursement = reimbursements[i];
			transactionsAppliedNew.push(...(reimbursement.transactions as number[]));
		}

		return transactionsAppliedNew;
	}, [reimbursements]);

	let urlFetchApplied = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&id=${transactionIds.join(',')}&fields=${fieldsTransactionsApplied}&populate=payments,payments.loan,payments.reimbursed.conversions`;

	if (paramDialogDateStart) {
		urlFetchApplied += `&dateStart=${paramDialogDateStart}`;
	}
	if (paramDialogDateEnd) {
		urlFetchApplied += `&dateEnd=${paramDialogDateEnd}`;
	}

	// fetch transactions applied
	const { isLoading: isLoadingTransactionsApplied, refetch: refetchTransactionsApplied } = useFetch(
		{
			isEnabled: transactionIds.length > 0,
			url: urlFetchApplied,
			options: {
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
			},
			onSuccess: (res) => {
				console.log('Transactions applied to reimbursements: ', res.data);
				if (res.data) {
					setTransactionsApplied(res.data);
				}
			},
		}
	);

	let totalApplied = 0;
	let totalReimbursed = 0;

	let currency: string | undefined = undefined;

	if (account) {
		currency = account.currency;
	}

	if (currency) {
		// calculate total applied
		totalApplied = transactionsApplied.reduce<number>((total, transaction) => {
			const payments = (transaction.payments || []) as Payment[];

			// sum amounts of the payments and use conversions if necessary
			const paymentsTotal = payments
				// filter payments that have been reimbursed
				.filter((p) => p.reimbursed?.amount)
				.reduce<number>((paymentTotal, payment) => {
					if (payment.currency === currency) {
						console.log('Payment is same currency as account', payment.amount);
						return paymentTotal + payment.amount;
					} else {
						// find conversion for the account currency
						const conversionForCurrency = payment.reimbursed?.conversions?.find(
							(c) => c.currency === currency
						);

						if (conversionForCurrency) {
							console.log(
								'Payment is different currency but has conversion',
								conversionForCurrency.amount
							);
							return paymentTotal + conversionForCurrency.amount;
						}
					}

					return paymentTotal;
				}, 0);

			return total + paymentsTotal;
		}, 0);

		// calculate total reimbursed
		totalReimbursed = reimbursements.reduce<number>((total, reimbursement) => {
			const payments = (reimbursement.payments || []) as Payment[];

			const paymentsTotal = payments.reduce<number>((paymentTotal, payment) => {
				if (payment.currency === currency) {
					return paymentTotal + payment.amount;
				} else {
					// find conversion for the account currency
					const conversionForCurrency = payment.reimbursed?.conversions?.find(
						(c) => c.currency === currency
					);

					if (conversionForCurrency) {
						return paymentTotal + conversionForCurrency.amount;
					}
				}

				return paymentTotal;
			}, 0);

			return total + paymentsTotal;
		}, 0);
	}

	// enhance reimbursements with transactions applied
	const reimbursementsEnhanced = reimbursements.map((reimbursement) => {
		const transactionsAppliedForReimbursement = transactionsApplied.filter((t) =>
			(reimbursement.transactions as number[])?.includes(t.id as number)
		);

		return {
			...reimbursement,
			transactions: transactionsAppliedForReimbursement,
		};
	});

	// clear stale data when account or reimbursementIds change
	const reimbursementIdsKey = reimbursementIds.join(',');
	useEffect(() => {
		setReimbursements([]);
		setTransactionsApplied([]);
	}, [account?.id, reimbursementIdsKey]);

	return {
		isLoadingReimbursements,
		isLoadingTransactionsApplied,
		refetchReimbursements,
		refetchTransactionsApplied,
		reimbursements: reimbursementsEnhanced,
		totalApplied,
		totalReimbursed,
		transactionsApplied,
	};
};
